// Framework-agnostic Cognito wrapper -- no React here. src/lib/AuthContext.tsx
// wraps these functions in a React context for the rest of the app.
//
// Auth model: no Hosted UI redirect. The SPA talks to the Cognito user pool
// directly via amazon-cognito-identity-js (SRP sign-in, native sign-up +
// confirmation-code flow). The backend's PostConfirmation Cognito trigger
// (backend/src/handlers/triggers/postConfirmation.ts) provisions the Users
// DynamoDB row the moment a sign-up is confirmed -- nothing here calls the
// API during sign-up/confirm.
import {
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
  CognitoUserSession,
  AuthenticationDetails,
} from "amazon-cognito-identity-js";

const USER_POOL_ID = import.meta.env.VITE_USER_POOL_ID;
const CLIENT_ID = import.meta.env.VITE_USER_POOL_CLIENT_ID;

let pool: CognitoUserPool | null = null;
function getPool(): CognitoUserPool {
  if (!pool) {
    pool = new CognitoUserPool({ UserPoolId: USER_POOL_ID, ClientId: CLIENT_ID });
  }
  return pool;
}

export interface IdClaims {
  sub: string;
  email?: string;
  name?: string;
  "cognito:groups"?: string[];
  // Present only for a federated sign-in (e.g. Facebook) -- Cognito encodes
  // it as a JSON string, not a native array, in the ID token.
  identities?: string;
}

/** Decode a CognitoUserSession's ID token payload without re-verifying the
 *  signature -- the backend is the source of truth for authorization; this
 *  is purely for the UI to decide what to show (nav links, admin section). */
export function getIdClaims(session: CognitoUserSession): IdClaims {
  return session.getIdToken().decodePayload() as IdClaims;
}

export type AuthProvider = "email" | "facebook";

/** Which sign-in method produced this session -- email/password (direct SRP)
 *  or a federated IdP. Only Facebook is wired up today (see facebookAuth.ts),
 *  so any federated identity found is reported as "facebook". */
export function getAuthProvider(claims: IdClaims): AuthProvider {
  if (!claims.identities) return "email";
  try {
    const identities = JSON.parse(claims.identities) as Array<{ providerName?: string }>;
    if (identities.some((i) => i.providerName === "Facebook")) return "facebook";
  } catch {
    // Malformed/unexpected shape -- fall back to email rather than guess.
  }
  return "email";
}

/** phone must already be E.164 ("+15095551234") -- Cognito rejects anything
 *  else. The PostConfirmation trigger copies it onto the Users row. */
export function signUp(email: string, password: string, phone: string): Promise<void> {
  const attributes = [new CognitoUserAttribute({ Name: "phone_number", Value: phone })];
  return new Promise((resolve, reject) => {
    getPool().signUp(email, password, attributes, [], (err) => {
      if (err) reject(err);
      else resolve();
    });
  });
}

/** clientMetadata is handed to the PostConfirmation trigger as-is (string
 *  values only) -- that's how the sign-up text-reminder choice reaches the
 *  Users row without being a Cognito attribute. */
export function confirmSignUp(
  email: string,
  code: string,
  clientMetadata?: Record<string, string>,
): Promise<void> {
  const user = new CognitoUser({ Username: email, Pool: getPool() });
  return new Promise((resolve, reject) => {
    user.confirmRegistration(
      code,
      true,
      (err) => {
        if (err) reject(err);
        else resolve();
      },
      clientMetadata,
    );
  });
}

export function resendConfirmationCode(email: string): Promise<void> {
  const user = new CognitoUser({ Username: email, Pool: getPool() });
  return new Promise((resolve, reject) => {
    user.resendConfirmationCode((err) => {
      if (err) reject(err);
      else resolve();
    });
  });
}

/** Starts the forgot-password flow: Cognito emails a reset code. */
export function forgotPassword(email: string): Promise<void> {
  const user = new CognitoUser({ Username: email, Pool: getPool() });
  return new Promise((resolve, reject) => {
    user.forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
}

/** Completes the forgot-password flow with the emailed code + a new password. */
export function confirmForgotPassword(email: string, code: string, newPassword: string): Promise<void> {
  const user = new CognitoUser({ Username: email, Pool: getPool() });
  return new Promise((resolve, reject) => {
    user.confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
}

export function signIn(email: string, password: string): Promise<CognitoUserSession> {
  const user = new CognitoUser({ Username: email, Pool: getPool() });
  const authDetails = new AuthenticationDetails({ Username: email, Password: password });
  return new Promise((resolve, reject) => {
    user.authenticateUser(authDetails, {
      onSuccess: (session) => resolve(session),
      onFailure: (err) => reject(err),
    });
  });
}

export function signOut(): void {
  const user = getPool().getCurrentUser();
  if (user) user.signOut();
}

/** Returns the current, valid (refreshed if needed) session, or null if
 *  nobody is signed in / the refresh token is gone/expired. Used on app
 *  mount to restore a session without asking the user to sign in again. */
export function getCurrentSession(): Promise<CognitoUserSession | null> {
  const user = getPool().getCurrentUser();
  if (!user) return Promise.resolve(null);
  return new Promise((resolve) => {
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) {
        resolve(null);
      } else {
        resolve(session);
      }
    });
  });
}

/** The current ID token (JWT string), or null if not signed in. Used by
 *  src/lib/api.ts to attach `Authorization: Bearer <idToken>`. */
export async function getIdToken(): Promise<string | null> {
  const session = await getCurrentSession();
  return session ? session.getIdToken().getJwtToken() : null;
}
